// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import DeliveryTruck from "@gcg/XDL/Icons/DeliveryTruck";
import Pickup from "@gcg/XDL/Icons/Pickup";
import WarningGlyph from "@gcg/XDL/Icons/WarningGlyph";
import WarningStock from "@gcg/XDL/Icons/WarningStock";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import getRealTimeWarehouseInventory from "@insite/client-framework/Store/Data/RealTimeInventory/Handlers/GetRealTimeWarehouseInventory";
import translate from "@insite/client-framework/Translate";
import { WarehouseDto } from "@insite/client-framework/Types/ApiModels";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import Icon, { IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
});

interface OwnProps {
    productId: string;
    unitOfMeasure: string;
    additionalResults?: any;
    extendedStyles?: ProductAvailabilityStyles;
    failedToLoadInventory?: boolean;
    isInStock?: boolean;
    isPartial?: boolean;
    isBackOrder?: boolean;
    fulfillmentMedthod?: string;
    qtyOrdered?: string;
    qtyOnHand?: number;
    isCustomGrouping?: boolean;
    trackInventory?: boolean;
    inventoryFallBack?: string;
}

const mapDispatchToProps = {
    getRealTimeWarehouseInventory,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ProductAvailabilityStyles {
    container?: InjectableCss;
    messageGridItem?: InjectableCss;
    availabilityByWarehouseLinkGridItem?: InjectableCss;
    inventoryMessage?: InjectableCss;
    messageText?: TypographyPresentationProps;
    realTimeText?: TypographyPresentationProps;
    errorWrapper?: InjectableCss;
    errorText?: TypographyPresentationProps;
    availabilityByWarehouseLink?: LinkPresentationProps;
    availabilityByWarehouseModal?: ModalPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    availabilityByWarehouseTable?: DataTableProps;
    availabilityByWarehouseTableHead?: DataTableHeadProps;
    availabilityByWarehouseTableHeader?: DataTableHeaderProps;
    availabilityByWarehouseTableBody?: DataTableBodyProps;
    availabilityByWarehouseTableRow?: DataTableRowProps;
    availabilityByWarehouseTableCell?: DataTableCellBaseProps;
    pickupMessage?: TypographyPresentationProps;
    containerWithAdditionalMessage?: InjectableCss;
    additionalMessageContainer?: InjectableCss;
    separatorStyle?: InjectableCss;
}

export const productAvailabilityStyles: ProductAvailabilityStyles = {
    container: {
        css: css`
            display: flex;
            & * {
                font-size: 14px;
                line-height: 18px;
                display: block;
            }
        `,
    },
    centeringWrapper: {
        css: css`
            height: 100px;
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        color: "primary.main",
        css: css`
            margin: 0 auto;
        `,
    },
    messageText: {
        size: "14px",
        lineHeight: "18px",
        weight: "normal",
        color: "text",
    },
    realTimeText: {
        size: "14px",
        lineHeight: "18px",
        weight: "normal",
        color: "text",
        css: css`
            display: inline-block;
            text-align: left;
            min-width: 25px;

            &::after {
                overflow: hidden;
                display: inline-block;
                vertical-align: bottom;
                animation: ellipsis steps(4, end) 900ms infinite 1s;
                content: "\\2026";
                width: 0;
            }

            @keyframes ellipsis {
                to {
                    width: 1.25em;
                }
            }
        `,
    },
    errorText: {
        size: "14px",
        lineHeight: "18px",
        weight: "normal",
        color: "text",
    },
    availabilityByWarehouseLink: {
        color: "text",
        css: css`
            font-size: 14px;
            line-height: 18px;
            font-weight: normal;
            text-decoration: underline;

            &.left-space {
                margin-left: 18px;
            }
        `,
    },
};

const CartLineAvailability: FC<Props> = ({
    productId,
    unitOfMeasure,
    failedToLoadInventory,
    getRealTimeWarehouseInventory,
    extendedStyles,
    isInStock,
    isPartial,
    isBackOrder,
    fulfillmentMedthod,
    productSettings,
    qtyOrdered,
    additionalResults,
    qtyOnHand,
    isCustomGrouping,
    trackInventory,
    inventoryFallBack,
}) => {
    const [modalIsOpen, setModalIsOpen] = React.useState(false);
    const [warehouses, setWarehouses] = React.useState<WarehouseDto[] | undefined>(undefined);
    const [errorMessage, setErrorMessage] = React.useState("");
    const [isLoading, setIsLoading] = React.useState(false);
    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };
    const viewAvailabilityByWarehouseClickHandler = () => {
        setIsLoading(true);
        setModalIsOpen(true);
        getRealTimeWarehouseInventory({
            productId,
            unitOfMeasure,
            expand: ["warehouses"],
            onComplete: (result: any) => {
                setWarehouses(result.warehouses);
                setErrorMessage(result.errorMessage);
                setIsLoading(false);
            },
        });
    };

    const [styles] = React.useState(() => mergeToNew(productAvailabilityStyles, extendedStyles));

    if (failedToLoadInventory) {
        return (
            <StyledWrapper {...styles.errorWrapper}>
                <Typography {...styles.errorText}>{siteMessage("RealTimeInventory_InventoryLoadFailed ")}</Typography>
            </StyledWrapper>
        );
    }

    const checkWarehousesModal = (
        <>
            {inventoryFallBack !== "true" && (
                <Link
                    {...styles.availabilityByWarehouseLink}
                    onClick={viewAvailabilityByWarehouseClickHandler}
                    data-test-selector="availabilityByWarehouseLink"
                >
                    {qtyOnHand === 0 ? translate("Check Lead Times") : translate("Check All Warehouses")}
                </Link>
            )}
            <Modal
                {...styles.availabilityByWarehouseModal}
                headline={translate("Warehouse Inventory")}
                isOpen={modalIsOpen}
                handleClose={modalCloseHandler}
            >
                {isLoading && (
                    <StyledWrapper {...styles.centeringWrapper}>
                        <LoadingSpinner {...styles.spinner} />
                    </StyledWrapper>
                )}
                {!isLoading && (
                    <>
                        {errorMessage ? (
                            <div>{errorMessage}</div>
                        ) : (
                            <DataTable
                                {...styles.availabilityByWarehouseTable}
                                data-test-selector="availabilityByWarehouseModal"
                            >
                                <DataTableHead {...styles.availabilityByWarehouseTableHead}>
                                    <DataTableHeader {...styles.availabilityByWarehouseTableHeader}>
                                        {translate("Warehouse")}
                                    </DataTableHeader>
                                    <DataTableHeader {...styles.availabilityByWarehouseTableHeader}>
                                        {translate("Qty On Hand")}
                                    </DataTableHeader>
                                    <DataTableHeader {...styles.availabilityByWarehouseTableHeader}>
                                        {translate("Typical Lead Time (Days)")}
                                    </DataTableHeader>
                                    <DataTableHeader {...styles.availabilityByWarehouseTableHeader}>
                                        {translate("Qty On Order")}
                                    </DataTableHeader>
                                </DataTableHead>
                                <DataTableBody {...styles.availabilityByWarehouseTableBody}>
                                    {warehouses?.map(warehouse => (
                                        <DataTableRow {...styles.availabilityByWarehouseTableRow} key={warehouse.name}>
                                            <DataTableCell {...styles.availabilityByWarehouseTableCell}>
                                                {warehouse.description || warehouse.name}
                                            </DataTableCell>
                                            <DataTableCell {...styles.availabilityByWarehouseTableCell}>
                                                {warehouse.qty === 0 ? "-" : warehouse.qty}
                                            </DataTableCell>
                                            <DataTableCell {...styles.availabilityByWarehouseTableCell}>
                                                {(warehouse as any)?.properties?.typicalLeadTime}
                                            </DataTableCell>
                                            <DataTableCell {...styles.availabilityByWarehouseTableCell}>
                                                {(warehouse as any)?.properties?.qtyOnOrder === "0"
                                                    ? "-"
                                                    : (warehouse as any)?.properties?.qtyOnOrder}
                                            </DataTableCell>
                                        </DataTableRow>
                                    ))}
                                </DataTableBody>
                            </DataTable>
                        )}
                    </>
                )}
            </Modal>
        </>
    );

    const inStockMessage = (
        <StyledWrapper {...styles.container}>
            <Icon
                src={fulfillmentMedthod === "ship" ? DeliveryTruck : Pickup}
                size={19}
                color="success.main"
                css={css`
                    margin-right: 5px;
                `}
            />
            {fulfillmentMedthod === "ship" ? <Typography>{translate("Available To Ship")}</Typography> : null}
            {fulfillmentMedthod === "pickup" ? <Typography>{translate("Available For Pick Up")}</Typography> : null}
        </StyledWrapper>
    );

    const partialMessage = (
        <StyledWrapper {...styles.container}>
            <Icon
                src={WarningStock}
                size={15}
                color="partial.main"
                css={css`
                    margin-right: 5px;
                `}
            />
            <StyledWrapper>
                {fulfillmentMedthod === "ship" ? (
                    <Typography>
                        {isCustomGrouping ? qtyOrdered : qtyOnHand} {translate("Available To Ship")}
                    </Typography>
                ) : null}
                {fulfillmentMedthod === "pickup" ? (
                    <Typography>
                        {additionalResults} {translate("Available For Pick Up")}
                    </Typography>
                ) : null}
                {productSettings.showInventoryAvailability &&
                    productSettings.displayInventoryPerWarehouse &&
                    trackInventory &&
                    checkWarehousesModal}
            </StyledWrapper>
        </StyledWrapper>
    );

    const backOrderMessage = (
        <StyledWrapper {...styles.container}>
            <Icon
                src={WarningGlyph}
                size={16}
                color="warning.main"
                css={css`
                    margin-right: 5px;
                `}
            />
            <StyledWrapper>
                <Typography>
                    {isCustomGrouping ? qtyOrdered : ""} {translate("On Backorder")}
                </Typography>
                {productSettings.showInventoryAvailability &&
                    productSettings.displayInventoryPerWarehouse &&
                    trackInventory &&
                    checkWarehousesModal}
            </StyledWrapper>
        </StyledWrapper>
    );

    return (
        <>
            {isInStock && inStockMessage}
            {isPartial && partialMessage}
            {isBackOrder && backOrderMessage}
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(CartLineAvailability);
